/**
 * URL auto-open for the /editor PDF a11y editor.
 *
 * Supports three query forms on /editor:
 *   1. ?job=<job_id>           — same-origin: fetch /api/editor-download/<id>
 *      (the user's own completed job; cookie/session auth is honored). This
 *      endpoint is a same-origin FileResponse, never a cross-origin GCS
 *      redirect — see docs/rca/week1a-editor-failed-to-fetch-260918.md.
 *      `?jobId=` is accepted as an alias (the SPA's "Review & edit" link
 *      uses ?jobId=, so both names must resolve to the same code path —
 *      otherwise the link no-ops on the editor side).
 *   2. ?url=<encoded_url>      — arbitrary URL: prompt the user, then fetch.
 *      Same-origin URLs go straight through; cross-origin URLs require an
 *      explicit confirm() because the user is delegating fetch credentials
 *      and we surface the source. CORS failures fall through to a clear
 *      "couldn't load — try drag-drop" status, never a silent dead end.
 *   3. ?filename=<name>        — readable share form: resolves to the
 *      most-recent COMPLETED job with that filename (server endpoint
 *      `/api/jobs/by-filename/{name}`), then drives the same job-fetch
 *      path as form 1. Skipped when ?job=/?jobId= or ?url= is also
 *      present — explicit ids win to keep the URL contract predictable.
 *
 * When form 1 or 2 is present and ?filename= is *also* present, the
 * filename string is used as the override for the saved filename (not
 * as a lookup key). This keeps backward compatibility with existing
 * `?job=…&filename=…` URLs.
 *
 * After a successful or failed load we replace the URL with `/editor` so the
 * job_id / source URL doesn't linger in browser history (auth-tied).
 *
 * The module exports pure helpers (rewriteDropboxUrl, urlToFilename, parseParams)
 * for testing. The orchestration entry — autoOpenFromLocation — receives a
 * dependency bag so tests can stub fetch / confirm / the loadFile sink.
 */
import { appendTokenIfPresent } from "./url-token.js";
/* ─── Pure helpers (unit-tested) ──────────────────────────────────────────── */
/**
 * Parse the editor's URL parameters from a URL or location-like object.
 * Returns null when no auto-open is requested (job and url both absent).
 */
export function parseParams(input) {
    let search;
    if (typeof input === "string") {
        try {
            search = new URL(input, "http://placeholder.local").search;
        }
        catch {
            return null;
        }
    }
    else if (input && typeof input === "object" && "search" in input) {
        search = input.search;
    }
    else {
        return null;
    }
    const params = new URLSearchParams(search);
    // Accept both ?job= and ?jobId= for the same param. The SPA "Review &
    // edit" link in app.js uses ?jobId=; older docs / direct URL builders
    // may use ?job=. Both must reach the same fetch path.
    const jobId = params.get("job") || params.get("jobId");
    const url = params.get("url");
    const filename = params.get("filename");
    // ?file=<relpath> is a dev-only convenience: route through
    // /api/editor/local-file (which is hard-disabled in staging/prod).
    const file = params.get("file");
    // ?filename= is a primary lookup form when neither ?job= nor ?url=
    // is present — autoOpenFromLocation resolves it via
    // /api/jobs/by-filename/{name} before driving the jobId path.
    // When ?job= or ?url= IS present alongside ?filename=, the filename
    // is just a saved-name override, NOT a lookup trigger.
    if (!jobId && !url && !filename && !file)
        return null;
    return {
        jobId: jobId || null,
        url: url || null,
        filename: filename || null,
        file: file || null,
    };
}
/**
 * Dropbox shared-link normaliser: ?dl=0 → ?dl=1 (raw bytes) and
 * www.dropbox.com → dl.dropboxusercontent.com (CORS-friendly host).
 * No-op for non-Dropbox URLs.
 */
export function rewriteDropboxUrl(url) {
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        return url;
    }
    const host = parsed.hostname.toLowerCase();
    if (host !== "www.dropbox.com" && host !== "dropbox.com")
        return url;
    parsed.searchParams.set("dl", "1");
    return parsed.toString();
}
/**
 * Derive a filename from a URL. Falls back to "document.pdf" when the URL
 * ends in a slash or has no usable path segment. Always ends in ".pdf".
 */
export function urlToFilename(url) {
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        return "document.pdf";
    }
    // eslint-disable-next-line no-restricted-syntax -- portable-path-string-split: URL.pathname always uses '/' (RFC 3986); not a filesystem path
    const segments = parsed.pathname.split("/").filter(Boolean);
    // WHY: .at(-1) returns T|undefined; ?? "" coerces to string for noUncheckedIndexedAccess
    const last = segments.at(-1) ?? "";
    let name;
    try {
        name = decodeURIComponent(last);
    }
    catch {
        name = last;
    }
    if (!name)
        return "document.pdf";
    return /\.pdf$/i.test(name) ? name : `${name}.pdf`;
}
/**
 * Same-origin check against the current location.
 */
export function isSameOrigin(url, location) {
    try {
        const parsed = new URL(url, location.origin);
        return parsed.origin === location.origin;
    }
    catch {
        return false;
    }
}
/* ─── Fetch helpers ──────────────────────────────────────────────────────── */
/**
 * Resolve a filename to its most-recent completed job_id via
 * `GET /api/jobs/by-filename/{name}`. Returns null on 404 (no completed
 * job for that name in the caller's scope); throws on other non-2xx
 * responses or network error.
 *
 * Forwards `?token=` so the workspace-gate / admin-bypass smoke flow
 * works the same way as fetchJobBytes / fetchEditorIR.
 */
export async function resolveFilenameToJobId(filename, fetchImpl) {
    const url = appendTokenIfPresent(`/api/jobs/by-filename/${encodeURIComponent(filename)}`);
    const resp = await fetchImpl(url, {
        credentials: "include",
        redirect: "follow",
    });
    if (resp.status === 404)
        return null;
    if (!resp.ok) {
        throw new Error(`Filename lookup returned HTTP ${resp.status}`);
    }
    const body = await resp.json();
    return body.id || body.job_id || null;
}
/**
 * Fetch the remediated output of a completed job. Returns a Uint8Array on
 * success; throws on non-2xx responses or network error.
 *
 * Uses the editor's own same-origin `/api/editor-download/<jobId>` endpoint
 * (a `FileResponse`/`Response`, never a redirect) rather than the generic
 * `/api/jobs/<id>/download` route, which on GCS deployments 302-redirects to
 * a cross-origin signed `storage.googleapis.com` URL — a credentialed CORS
 * fetch that follows that redirect rejects with a bare
 * TypeError("Failed to fetch") when the bucket has no CORS config. This is
 * the PRIMARY (root) fix; see docs/rca/week1a-editor-failed-to-fetch-260918.md.
 * The CORS/network error wrap below is retained as defense-in-depth for any
 * other network failure mode (offline, DNS, etc.), and also aligns this
 * helper's byte source with the editor's IR fetch, which already uses
 * editor-download (see `interop.ts` `serializeToBytes`).
 */
export async function fetchJobBytes(jobId, fetchImpl) {
    const url = appendTokenIfPresent(`/api/editor-download/${encodeURIComponent(jobId)}`);
    let resp;
    try {
        resp = await fetchImpl(url, {
            credentials: "include",
            redirect: "follow",
        });
    }
    catch (err) {
        // WHY: defense-in-depth — matching the sibling fetchUrlBytes /
        // fetchLocalFileBytes helpers — so the loading overlay never surfaces
        // the raw browser string for any other network failure. See
        // docs/rca/week1a-editor-failed-to-fetch-260918.md.
        const msg = err instanceof Error ? err.message : "fetch failed";
        throw new Error(`Could not download this job (network or CORS error): ${msg}. Try downloading it from the results page and dropping the file into the editor.`);
    }
    if (!resp.ok) {
        throw new Error(`Job download returned HTTP ${resp.status}`);
    }
    const buf = await resp.arrayBuffer();
    return new Uint8Array(buf);
}
/**
 * Fetch a local-file PDF via /api/editor/local-file. Dev-only — the
 * server-side endpoint hard-rejects when ADA_TOOL_ENV_LABEL is staging/prod.
 *
 * Surfaces server-side reason strings (403/400/404) on the exception so the
 * caller can show a non-blocking toast / banner.
 */
export async function fetchLocalFileBytes(relPath, fetchImpl) {
    const url = `/api/editor/local-file?path=${encodeURIComponent(relPath)}`;
    let resp;
    try {
        resp = await fetchImpl(url, { credentials: "include" });
    }
    catch (err) {
        const msg = err instanceof Error ? err.message : "fetch failed";
        throw new Error(`Could not fetch ${relPath}: ${msg}`);
    }
    if (!resp.ok) {
        let detail = "";
        try {
            const body = await resp.json();
            detail = body && body.detail ? String(body.detail) : "";
        }
        catch {
            // JSON parse failure — no extra context to surface
        }
        if (resp.status === 403) {
            throw new Error(`?file= is dev-only and disabled in this environment.${detail ? " " + detail : ""}`);
        }
        if (resp.status === 404) {
            throw new Error(`File not found: ${relPath}`);
        }
        if (resp.status === 400) {
            throw new Error(`?file= rejected: ${detail || "path not allowed"}`);
        }
        throw new Error(`local-file fetch returned HTTP ${resp.status}${detail ? ": " + detail : ""}`);
    }
    const buf = await resp.arrayBuffer();
    return new Uint8Array(buf);
}
/**
 * Fetch arbitrary URL bytes. Tries CORS; on failure surfaces a clear
 * exception message that the caller can propagate to the loading overlay.
 *
 * Cookies are NOT sent (`credentials: 'omit'`) — we don't want to
 * exfiltrate session credentials to a third-party origin from a URL the
 * page just loaded.
 */
export async function fetchUrlBytes(url, fetchImpl) {
    let resp;
    try {
        resp = await fetchImpl(url, {
            mode: "cors",
            credentials: "omit",
            redirect: "follow",
        });
    }
    catch (err) {
        const msg = err instanceof Error ? err.message : "fetch failed";
        throw new Error(`Could not fetch ${url}: ${msg}. The source may block cross-origin downloads — please download the file and drop it into the editor instead.`);
    }
    if (!resp.ok) {
        throw new Error(`Source returned HTTP ${resp.status} for ${url}`);
    }
    const buf = await resp.arrayBuffer();
    return new Uint8Array(buf);
}
/* ─── Orchestration ──────────────────────────────────────────────────────── */
/**
 * Replace the current URL with a clean /editor path so the job_id / source URL
 * doesn't linger in browser history. Optionally retains the filename in the
 * URL hash (non-secret) for a deep-link-back UX.
 */
export function cleanUrl(history, filename) {
    // Preserve `?token=` across the cleanup so subsequent API calls
    // (fetchEditorIR, postEditorEdit) can still authenticate as admin.
    // Stripping `?jobId=` / `?url=` is the actual goal — those would
    // re-trigger auto-open on refresh; the token doesn't.
    let search = "";
    if (typeof window !== "undefined" && window.location && window.location.search) {
        const params = new URLSearchParams(window.location.search);
        const token = params.get("token");
        if (token)
            search = `?token=${encodeURIComponent(token)}`;
    }
    const hash = filename ? `#filename=${encodeURIComponent(filename)}` : "";
    try {
        history.replaceState(null, "", `/editor${search}${hash}`);
    }
    catch {
        // history API unavailable (very old browser / sandboxed iframe);
        // not load-bearing for the rest of the editor.
    }
}
/**
 * Top-level orchestration. Looks at `location.search`, fetches bytes from
 * the appropriate source, and hands a File to `deps.loadFile`. Updates UI
 * via deps.{showLoading,setLoadingLabel,showLoadingError,hideLoading} on
 * the way through.
 *
 * Returns true when an auto-open was attempted (success or error), false
 * when the URL had no params worth acting on (caller can fall through to
 * the empty-state UX).
 *
 * WHY narrower types than full History / typeof fetch:
 * per frontend-monkeypatch-to-DI-260525.md §6.4 (Shape C) — once
 * url-autoopen.js migrates to .ts the types can be locked; for now
 * the narrower JSDoc lets .ts tests pass stubs without `as unknown as`
 * double-casts (the cast-storm class the Epic targets).
 */
export async function autoOpenFromLocation(deps) {
    const params = parseParams(deps.location);
    if (!params)
        return false;
    const { url, filename, file } = params;
    let { jobId } = params;
    try {
        // ?file=<relpath> (dev-only) — wins over ?filename=/?jobId= when
        // present so refresh of a file= URL stays deterministic.
        if (file) {
            deps.showLoading(`Loading ${file}…`);
            const bytes = await fetchLocalFileBytes(file, deps.fetch);
            // eslint-disable-next-line no-restricted-syntax -- portable-path-string-split: dev-only ?file= param is a forward-slash path; Windows is not a supported dev env for editor fixtures
            const segs = file.split("/");
            const name = filename || segs.at(-1) || "document.pdf";
            const fileObj = new File([bytes.buffer], name, { type: "application/pdf" });
            // Don't strip ?file= from the URL — keeping it means refresh
            // reloads the same fixture (the spec contract).
            await deps.loadFile(fileObj);
            return true;
        }
        // ?filename=… (without ?job=/?jobId=/?url=): resolve the name
        // to a jobId, then fall through to the existing jobId path.
        // When ?job= or ?url= is also present, the filename is just an
        // override on the saved File name (back-compat).
        if (filename && !jobId && !url) {
            deps.showLoading(`Looking up ${filename}…`);
            const resolved = await resolveFilenameToJobId(filename, deps.fetch);
            if (!resolved) {
                deps.showLoadingError(`No completed remediation found for ${filename}.`);
                cleanUrl(deps.history, null);
                return true;
            }
            jobId = resolved;
        }
        if (jobId) {
            deps.showLoading(`Loading job ${jobId.slice(0, 8)}…`);
            const bytes = await fetchJobBytes(jobId, deps.fetch);
            const name = filename || `${jobId}.pdf`;
            const fileObj = new File([bytes.buffer], name, { type: "application/pdf" });
            cleanUrl(deps.history, name);
            await deps.loadFile(fileObj);
            return true;
        }
        if (url) {
            const sameOrigin = isSameOrigin(url, deps.location);
            const fetchUrl = rewriteDropboxUrl(url);
            if (!sameOrigin) {
                const ok = deps.confirm(`Load this PDF from another site?\n\n${url}\n\nThe editor will fetch the file directly. Cookies are not sent.`);
                if (!ok) {
                    cleanUrl(deps.history, null);
                    return true;
                }
            }
            deps.showLoading(`Loading ${url}…`);
            const bytes = await fetchUrlBytes(fetchUrl, deps.fetch);
            const name = filename || urlToFilename(url);
            const fileObj = new File([bytes.buffer], name, { type: "application/pdf" });
            cleanUrl(deps.history, name);
            await deps.loadFile(fileObj);
            return true;
        }
    }
    catch (err) {
        const msg = err instanceof Error ? err.message : "Auto-open failed";
        deps.showLoadingError(msg);
        // Always clean the URL even on error: keeping ?job=... or ?url=... in
        // history would re-trigger the same failure on every refresh.
        cleanUrl(deps.history, null);
        return true;
    }
    return false;
}
